import type { User } from '@/types';

import { usesMomentsScheduling } from '@/features/appointments/schedulingProvider';
import { isAdmin, isCoach } from '@/types/user';

export type UpcomingNativeSource =
  | { readonly kind: 'participant'; readonly userId: string }
  | { readonly kind: 'coach'; readonly userId: string }
  | { readonly kind: 'member-scope'; readonly memberId: string }
  | { readonly kind: 'none' };

export interface UpcomingViewScope {
  readonly actingRole?: 'member' | 'coach';
  readonly remountKey: string;
  readonly viewer: User;
  readonly isOverview: boolean;
  readonly legacyUserId: string;
  readonly nativeSource: UpcomingNativeSource;
}

interface DeriveUpcomingViewScopeInput {
  readonly viewer: User;
  readonly isCoachPath?: boolean;
  readonly isOverview: boolean;
  readonly selectedMemberId: string;
}

const hasActiveMomentsCoach = (viewer: User): boolean =>
  viewer.coaches.some(
    (coach) =>
      usesMomentsScheduling(coach) &&
      viewer.coachAssignments?.some(
        (assignment) =>
          assignment.coachId === coach.id && assignment.endedAt === null,
      ),
  );

export const deriveUpcomingViewScope = ({
  viewer,
  isCoachPath,
  isOverview,
  selectedMemberId,
}: DeriveUpcomingViewScopeInput): UpcomingViewScope => {
  const viewerUserId = viewer.id;
  // The default retains the legacy self-route behavior for callers that have
  // not supplied an actor context. Canonical pages always supply the pathname.
  const routeIsCoach = isCoachPath ?? isCoach(viewer);
  // The pathname selects the self-view actor. Keep the selected-member
  // overview's established global-role behavior unchanged.
  let actingRole: UpcomingViewScope['actingRole'];
  if (isOverview) {
    actingRole = isCoach(viewer) && !isAdmin(viewer) ? 'coach' : undefined;
  } else if (routeIsCoach) {
    actingRole = 'coach';
  } else if (isCoachPath !== undefined) {
    actingRole = 'member';
  }

  let nativeSource: UpcomingNativeSource = { kind: 'none' };

  if (
    !isOverview &&
    !selectedMemberId &&
    viewerUserId &&
    routeIsCoach &&
    isCoach(viewer) &&
    usesMomentsScheduling(viewer)
  ) {
    nativeSource = { kind: 'coach', userId: viewerUserId };
  } else if (
    !isOverview &&
    !selectedMemberId &&
    viewerUserId &&
    !routeIsCoach &&
    hasActiveMomentsCoach(viewer)
  ) {
    nativeSource = { kind: 'participant', userId: viewerUserId };
  } else if (
    isOverview &&
    selectedMemberId &&
    viewerUserId &&
    (isAdmin(viewer) || isCoach(viewer))
  ) {
    nativeSource = { kind: 'member-scope', memberId: selectedMemberId };
  }

  let routeScope = 'self';
  if (isOverview) {
    routeScope = 'overview';
  } else if (isCoachPath !== undefined) {
    routeScope = routeIsCoach ? 'coach' : 'member';
  }

  return {
    ...(actingRole ? { actingRole } : {}),
    isOverview,
    legacyUserId: selectedMemberId || viewerUserId,
    nativeSource,
    remountKey: JSON.stringify([
      routeScope,
      viewerUserId,
      selectedMemberId,
      nativeSource.kind,
    ]),
    viewer,
  };
};
